import React, { useState, useRef } from 'react';
import { BACKEND_URL } from '../config';
import axios from 'axios';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Progress } from './ui/progress';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { 
  Upload, 
  Download, 
  FileText, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  Loader2,
  FileSpreadsheet
} from 'lucide-react';
import { toast } from 'sonner';

const API = BACKEND_URL;

const CSVImportDialog = ({ open, onClose, onImportComplete }) => {
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState(null);
  const [progress, setProgress] = useState(0);
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.csv')) {
        toast.error('Please select a CSV file');
        return;
      }
      setFile(selectedFile);
      setResults(null);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.name.endsWith('.csv')) {
      setFile(droppedFile);
      setResults(null);
    } else {
      toast.error('Please drop a CSV file');
    }
  };

  const downloadTemplate = async () => {
    try {
      const response = await axios.get(`${API}/api/social-actors/import-csv/template`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'social_actors_import_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Template downloaded successfully');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleImport = async () => {
    if (!file) {
      toast.error('Please select a file first');
      return;
    }

    setImporting(true);
    setProgress(10);

    try {
      const formData = new FormData();
      formData.append('file', file);

      setProgress(30);

      const response = await axios.post(`${API}/api/social-actors/import-csv`, formData, {
        headers: { 
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setProgress(100);
      setResults(response.data);

      if (response.data.imported > 0) {
        toast.success(`Successfully imported ${response.data.imported} organizations`);
        if (onImportComplete) {
          onImportComplete();
        }
      } else if (response.data.skipped > 0) {
        toast.warning('No new organizations imported - all records were duplicates or invalid');
      }
    } catch (error) {
      console.error('Import error:', error);
      toast.error(error.response?.data?.detail || 'Failed to import CSV');
      setResults({
        error: error.response?.data?.detail || 'Import failed',
        total_rows: 0,
        imported: 0,
        skipped: 0,
        errors: []
      });
    } finally {
      setImporting(false);
    }
  };

  const resetDialog = () => {
    setFile(null);
    setResults(null);
    setProgress(0);
  };

  const handleClose = () => {
    resetDialog();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5 text-emerald-700" />
            Import Social Actors from CSV
          </DialogTitle>
          <DialogDescription>
            Upload a CSV file to bulk import social actor profiles. Download the template for the correct format.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {/* Template Download */}
          <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-center gap-3">
              <Download className="h-5 w-5 text-blue-600" />
              <div>
                <p className="font-medium text-blue-900">Download Template</p>
                <p className="text-sm text-blue-700">Get the CSV template with example data</p>
              </div>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={downloadTemplate}
              className="border-blue-300 text-blue-700 hover:bg-blue-100"
              data-testid="download-template-btn"
            >
              <Download className="h-4 w-4 mr-1" />
              Download
            </Button>
          </div>

          {/* File Upload Area */}
          {!results && (
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                file ? 'border-emerald-400 bg-emerald-50' : 'border-gray-300 hover:border-emerald-400'
              }`}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              data-testid="file-drop-zone"
            >
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept=".csv"
                className="hidden"
                data-testid="file-input"
              />
              
              {file ? (
                <div className="space-y-2">
                  <FileText className="h-12 w-12 text-emerald-700 mx-auto" />
                  <p className="font-medium text-emerald-800">{file.name}</p>
                  <p className="text-sm text-emerald-700">
                    {(file.size / 1024).toFixed(1)} KB
                  </p>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setFile(null);
                    }}
                    className="text-red-600 hover:text-red-700"
                  >
                    Remove
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  <Upload className="h-12 w-12 text-gray-500 mx-auto" />
                  <p className="font-medium text-gray-700">
                    Drag & drop your CSV file here
                  </p>
                  <p className="text-sm text-gray-500">
                    or click to browse
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Progress Bar */}
          {importing && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Importing...</span>
                <span className="text-gray-600">{progress}%</span>
              </div>
              <Progress value={progress} className="h-2" />
            </div>
          )}

          {/* Results */}
          {results && !results.error && (
            <div className="space-y-4">
              {/* Summary Cards */}
              <div className="grid grid-cols-3 gap-4">
                <Card className="bg-gray-50">
                  <CardContent className="pt-4 text-center">
                    <p className="text-2xl font-bold text-gray-800">{results.total_rows}</p>
                    <p className="text-sm text-gray-600">Total Rows</p>
                  </CardContent>
                </Card>
                <Card className="bg-emerald-50">
                  <CardContent className="pt-4 text-center">
                    <p className="text-2xl font-bold text-emerald-700">{results.imported}</p>
                    <p className="text-sm text-emerald-700">Imported</p>
                  </CardContent>
                </Card>
                <Card className="bg-amber-50">
                  <CardContent className="pt-4 text-center">
                    <p className="text-2xl font-bold text-amber-600">{results.skipped}</p>
                    <p className="text-sm text-amber-700">Skipped</p>
                  </CardContent>
                </Card>
              </div>

              {/* Imported Organizations */}
              {results.imported_actors && results.imported_actors.length > 0 && (
                <div className="border rounded-lg p-4">
                  <h4 className="font-medium text-emerald-800 mb-3 flex items-center gap-2">
                    <CheckCircle className="h-4 w-4" />
                    Imported Organizations
                  </h4>
                  <div className="max-h-40 overflow-y-auto space-y-2">
                    {results.imported_actors.map((actor, idx) => (
                      <div key={idx} className="flex items-center justify-between text-sm p-2 bg-emerald-50 rounded">
                        <span>{actor.organization_name}</span>
                        <div className="flex items-center gap-2">
                          {actor.geocoded && (
                            <Badge variant="outline" className="text-xs">Geocoded</Badge>
                          )}
                          <Badge className="bg-emerald-100 text-emerald-700">Imported</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Errors */}
              {results.errors && results.errors.length > 0 && (
                <div className="border border-amber-200 rounded-lg p-4 bg-amber-50">
                  <h4 className="font-medium text-amber-800 mb-3 flex items-center gap-2">
                    <AlertCircle className="h-4 w-4" />
                    Skipped Rows ({results.errors.length})
                  </h4>
                  <div className="max-h-40 overflow-y-auto space-y-2">
                    {results.errors.map((err, idx) => (
                      <div key={idx} className="flex items-center justify-between text-sm p-2 bg-white rounded">
                        <span className="text-gray-700">Row {err.row}: {err.data || 'Unknown'}</span>
                        <span className="text-amber-600 text-xs">{err.error}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Error State */}
          {results && results.error && (
            <div className="border border-red-200 rounded-lg p-4 bg-red-50">
              <div className="flex items-center gap-2 text-red-700">
                <XCircle className="h-5 w-5" />
                <span className="font-medium">{results.error}</span>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={handleClose} data-testid="cancel-import-btn">
              {results ? 'Close' : 'Cancel'}
            </Button>
            {!results && (
              <Button 
                onClick={handleImport} 
                disabled={!file || importing}
                className="bg-emerald-700 hover:bg-emerald-800"
                data-testid="import-btn"
              >
                {importing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Importing...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    Import CSV
                  </>
                )}
              </Button>
            )}
            {results && !results.error && (
              <Button 
                onClick={resetDialog}
                variant="outline"
                data-testid="import-another-btn"
              >
                Import Another
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CSVImportDialog;
